import { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import FlowLoader from "./FlowLoader";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

const ZoneStorageSettingsChangeConfirmation = ({ warehouse, activeBatch, set_settingsPopupActive, changeSorterOnePopup }: { warehouse: string, activeBatch: number, set_settingsPopupActive: React.Dispatch<React.SetStateAction<boolean>>, changeSorterOnePopup: (newDate: string, newBatch: number) => void }) => {

    const navigator = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [checks, set_checks] = useState<{ [key: string]: any }>({ firstCheck: false, secondCheck: false });
    const [newSettings, set_newSettings] = useState<{ [key: string]: any }>({ newBatch: 0, newDate: "" });
    const [selectedNewBatch, set_selectedNewBatch] = useState<number>(1);
    const [selectedNewDate, set_selectedNewDate] = useState<string>("");
    const [sorterOneFinished, set_sorterOneFinished] = useState<{ [key: string]: boolean }>(activeBatch === 1 ? { askedQuestion: false, answer: false } : { askedQuestion: true, answer: false });
    const [showLoading, set_showLoading] = useState<boolean>(false);
    const [finalMessage, set_finalMessage] = useState<string>("");


    const handleCheckDecision = async (decisionNumber: number, decision: boolean) => {
        if (decision === false) {
            set_settingsPopupActive(false);
            return;
        }

        if (decisionNumber === 1) {
            set_checks({ ...checks, firstCheck: true });
        } else {
            set_checks({ ...checks, secondCheck: true });

            // Send off new batch stuff
            set_showLoading(true);

            await apiFetch(`/zonestorage/settings/end-sorter-one`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ warehouse: warehouse, newDate: newSettings.newDate, newBatch: newSettings.newBatch, sorterFinished: sorterOneFinished.answer, reauctionTag: checks.tagForReauction })
            }).then(async (res): Promise<any> => {

                set_showLoading(false);

                if (res.ok) {
                    set_finalMessage(t("change_success"));
                    setTimeout(() => changeSorterOnePopup(newSettings.newDate, newSettings.newBatch), 3000);
                } else if (res.status === 401) {
                    console.log("INVALID PERMISSIONS");
                    logoutProcessor();
                    navigator("/login");
                } else {
                    const response = await res.json();
                    set_finalMessage(response.message);
                    console.log(response.message);
                    toast.error(response.message);
                }
            })
        }
    }

    const handlePPdQuesiton = (answer: boolean) => {
        set_sorterOneFinished({ askedQuestion: true, answer: answer });

        if (answer === false) {
            set_checks({ ...checks, tagForReauction: "TEMP", tagForSingleOrders: "TEMP" });
        }
    };

    const handleSettingsChange = (settingToSet: string) => {
        console.log("Handling settings change:", settingToSet);
        if (settingToSet === "batch") {
            console.log("Setting Batch");
            set_newSettings({ ...newSettings, newBatch: selectedNewBatch });
        } else {
            set_newSettings({ ...newSettings, newDate: selectedNewDate });
        }
    };

    return (
        <div className="modal-overlay" onClick={() => set_settingsPopupActive(false)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("change_s1_settings")}</h2>
                    <button className="modal-close" onClick={() => set_settingsPopupActive(false)}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                <div className="zone-settings-popup-info">
                    <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: "var(--color-text-muted)", textAlign: "center" }}>{t("warehouse_name", { warehouse })}</p>
                    <div className="zone-settings-popup-info-details">
                        <p>{t("new_date_label", { date: newSettings.newDate === "" ? "?" : newSettings.newDate })}</p>
                        <p>{t("new_batch_label", { batch: newSettings.newBatch === 0 ? "?" : String(newSettings.newBatch) })}</p>
                        {(sorterOneFinished.askedQuestion === true && sorterOneFinished.answer === true) && <p>{t("reauction_tag", { tag: checks.tagForReauction === "" ? "?" : checks.tagForReauction })}</p>}
                    </div>
                </div>

                {checks.firstCheck === false && (
                    <div className="zone-settings-check-container">
                        <p style={{ fontWeight: 700, margin: 0 }}>{t("confirm_change")}</p>
                        <div className="modal-btn-group">
                            <button className="btn btn-success" onClick={(e) => { e.preventDefault(); handleCheckDecision(1, true) }}>{t("yes")}</button>
                            <button className="btn btn-ghost" onClick={(e) => { e.preventDefault(); handleCheckDecision(1, false) }}>{t("no")}</button>
                        </div>
                    </div>
                )}

                {(checks.firstCheck === true && sorterOneFinished.askedQuestion === false) && (
                    <div className="zone-settings-check-container">
                        <p style={{ fontWeight: 700, margin: 0 }}>{t("sorter_finished_question")}</p>
                        <div className="modal-btn-group">
                            <button className="btn btn-success" onClick={(e) => { e.preventDefault(); handlePPdQuesiton(true) }}>{t("finished")}</button>
                            <button className="btn btn-secondary" onClick={(e) => { e.preventDefault(); handlePPdQuesiton(false) }}>{t("quick_switch")}</button>
                        </div>
                    </div>
                )}

                {(checks.firstCheck === true && sorterOneFinished.askedQuestion === true && newSettings.newDate === "") && (
                    <div className="zone-settings-check-container">
                        <p style={{ fontWeight: 700, margin: 0 }}>{t("enter_new_date")}</p>
                        <form className="modal-form" onSubmit={(e) => { e.preventDefault(); handleSettingsChange("date") }}>
                            <input type="date" value={selectedNewDate} onChange={(e) => set_selectedNewDate(e.target.value)} autoFocus />
                            <button type="submit" className="btn btn-primary btn-full">{t("set_date")}</button>
                        </form>
                    </div>
                )}

                {(newSettings.newDate !== "" && newSettings.newBatch === 0) && (
                    <div className="zone-settings-check-container">
                        <p style={{ fontWeight: 700, margin: 0 }}>{t("enter_new_batch")}</p>
                        <form className="modal-form" onSubmit={(e) => { e.preventDefault(); handleSettingsChange("batch") }}>
                            <input type="number" min={1} max={4} value={selectedNewBatch} onChange={(e) => set_selectedNewBatch(Number(e.target.value))} autoFocus />
                            <button type="submit" className="btn btn-primary btn-full">{t("set_batch")}</button>
                        </form>
                    </div>
                )}

                {(newSettings.newBatch !== 0 && checks.secondCheck === false) && (
                    <div className="zone-settings-check-container">
                        <p style={{ fontWeight: 700, margin: 0 }}>{t("confirm_proceed")}</p>
                        <div className="modal-btn-group">
                            <button
                                className="btn btn-success"
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={(e) => { e.preventDefault(); handleCheckDecision(2, true) }}
                            >
                                {t("yes")}
                            </button>
                            <button className="btn btn-ghost" onClick={(e) => { e.preventDefault(); handleCheckDecision(2, false) }}>{t("no")}</button>
                        </div>
                    </div>
                )}

                {showLoading && <div className="flex"><FlowLoader /></div>}

                {finalMessage && <div>{finalMessage}</div>}

                </div>
            </div>
        </div>
    )
};

export default ZoneStorageSettingsChangeConfirmation;
